import type {
  ConversationTriage,
  ConversationTriageState,
  TriageTemplate,
} from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import type {
  ConversationTriageDataInput,
  SetDefaultConversationTriageInput,
  SoftDeleteConversationTriageInput,
} from 'src/gql/graphql';
import {
  cloneTriageActionConfigSnapshot,
  defaultTriageActionConfigSnapshot,
  parseTriageActionConfig,
} from 'src/graphql/entities/conversation-triage/conversation-triage.mappings';
import {
  CREATE_CONVERSATION_TRIAGE,
  SET_DEFAULT_CONVERSATION_TRIAGE,
  SOFT_DELETE_CONVERSATION_TRIAGE,
  UPDATE_CONVERSATION_TRIAGE,
} from 'src/graphql/entities/conversation-triage/conversation-triage.operations';
import {
  SetDefaultConversationTriageMutation,
  SoftDeleteConversationTriageMutation,
} from 'src/graphql/entities/conversation-triage/conversation-triage.types';
import { ConditionInput } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks/use-notifications';

const CREATE_TRIAGE_CONFIG_ERROR_MESSAGE = 'Failed to create configuration, please try again';
const RENAME_TRIAGE_CONFIG_ERROR_MESSAGE = 'Failed to rename configuration, please try again';
const SET_DEFAULT_TRIAGE_CONFIG_ERROR_MESSAGE =
  'Failed to set default configuration, please try again';
const DELETE_TRIAGE_CONFIG_ERROR_MESSAGE = 'Failed to delete configuration, please try again';
const DELETE_TRIAGE_CONFIG_SUCCESS_MESSAGE = 'Configuration deleted';

export interface ManageTriageConfigsOptions {
  partnerId: number | null;
  template: TriageTemplate;
  /**
   * Whether the partner already owns at least one non-deleted record. Drives
   * `isDefault` on create: runtime selection filters on `isDefault`, so a
   * partner whose first row is not the default silently gets no triage at all
   * (non-BPO) or the platform config (BPO) while their own record sits unused.
   */
  hasConfigurations: boolean;
  /** Called after every mutation completes; the list is non-normalized so cache patching is unsafe. */
  refetch: () => Promise<unknown>;
}

export const useManageTriageConfigs = ({
  partnerId,
  template,
  hasConfigurations,
  refetch,
}: ManageTriageConfigsOptions) => {
  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const makeOnError = useCallback(
    (message: string) => (error: unknown) => {
      console.error(error);
      showErrorAlert(message);
    },
    [showErrorAlert],
  );

  const [createConversationTriageMutation, { loading: isCreating }] = useMutation<
    { createConversationTriage: ConversationTriage[] },
    { data?: ConversationTriageDataInput }
  >(CREATE_CONVERSATION_TRIAGE, { onError: makeOnError(CREATE_TRIAGE_CONFIG_ERROR_MESSAGE) });

  const [updateConversationTriageMutation, { loading: isRenaming }] = useMutation<
    { updateConversationTriage: ConversationTriage[] },
    { conditions?: ConditionInput[]; data?: ConversationTriageDataInput }
  >(UPDATE_CONVERSATION_TRIAGE, { onError: makeOnError(RENAME_TRIAGE_CONFIG_ERROR_MESSAGE) });

  const [setDefaultConversationTriageMutation, { loading: isSettingDefault }] = useMutation<
    SetDefaultConversationTriageMutation,
    { input: SetDefaultConversationTriageInput }
  >(SET_DEFAULT_CONVERSATION_TRIAGE, {
    onError: makeOnError(SET_DEFAULT_TRIAGE_CONFIG_ERROR_MESSAGE),
  });

  const [softDeleteConversationTriageMutation, { loading: isDeleting }] = useMutation<
    SoftDeleteConversationTriageMutation,
    { input: SoftDeleteConversationTriageInput }
  >(SOFT_DELETE_CONVERSATION_TRIAGE, {
    onError: makeOnError(DELETE_TRIAGE_CONFIG_ERROR_MESSAGE),
  });

  const createFromDefaults = useCallback(async (): Promise<ConversationTriage | null> => {
    if (partnerId === null) {
      return null;
    }
    const snapshot = defaultTriageActionConfigSnapshot(
      template.tools,
      template.defaultVisibility,
    );
    const { data } = await createConversationTriageMutation({
      variables: {
        data: {
          partnerId,
          instructions: template.instructions,
          actionConfig: JSON.stringify(snapshot),
          llmConfig: template.llmConfig,
          isDefault: !hasConfigurations,
        },
      },
    });
    await refetch();
    return data?.createConversationTriage?.[0] ?? null;
  }, [partnerId, template, hasConfigurations, createConversationTriageMutation, refetch]);

  const duplicateConfig = useCallback(
    async (source: ConversationTriageState): Promise<ConversationTriage | null> => {
      if (partnerId === null) {
        return null;
      }
      const snapshot = cloneTriageActionConfigSnapshot(
        parseTriageActionConfig(source.actionConfig),
        template.tools,
        template.defaultVisibility,
      );
      const { data } = await createConversationTriageMutation({
        variables: {
          data: {
            partnerId,
            instructions: source.instructions,
            actionConfig: JSON.stringify(snapshot),
            llmConfig: source.llmConfig,
            isDefault: !hasConfigurations,
          },
        },
      });
      await refetch();
      return data?.createConversationTriage?.[0] ?? null;
    },
    [
      partnerId,
      template.tools,
      template.defaultVisibility,
      hasConfigurations,
      createConversationTriageMutation,
      refetch,
    ],
  );

  const renameConfig = useCallback(
    async (conversationTriageId: string, name: string) => {
      await updateConversationTriageMutation({
        variables: {
          conditions: [{ attribute: 'id', operator: '=', value: conversationTriageId }],
          data: { name },
        },
      });
      await refetch();
    },
    [updateConversationTriageMutation, refetch],
  );

  const setDefaultConfig = useCallback(
    async (conversationTriageId: string) => {
      if (partnerId === null) {
        return;
      }
      await setDefaultConversationTriageMutation({
        variables: { input: { conversationTriageId, partnerId } },
      });
      await refetch();
    },
    [partnerId, setDefaultConversationTriageMutation, refetch],
  );

  const softDeleteConfig = useCallback(
    async (conversationTriageId: string) => {
      if (partnerId === null) {
        return;
      }
      const { data } = await softDeleteConversationTriageMutation({
        variables: { input: { conversationTriageId, partnerId } },
      });
      await refetch();
      if (data?.softDeleteConversationTriage) {
        showSuccessAlert(DELETE_TRIAGE_CONFIG_SUCCESS_MESSAGE);
      }
    },
    [partnerId, softDeleteConversationTriageMutation, refetch, showSuccessAlert],
  );

  return {
    createFromDefaults,
    duplicateConfig,
    renameConfig,
    setDefaultConfig,
    softDeleteConfig,
    isMutating: isCreating || isRenaming || isSettingDefault || isDeleting,
  };
};
